import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import SitemarkIcon from "../components/landing/SitemarkIcon";
import ColorModeIconDropdown from "../theme/ColorModeIconDropdown";

const EFFECTIVE_DATE = "April 1, 2026";
const COMPANY_NAME = "Savac Plus";
const CONTACT_EMAIL = "legal@savacplus.com";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: "text.primary" }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function Para({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.8, mb: 1.5 }}>
      {children}
    </Typography>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <Box component="ul" sx={{ pl: 3, mb: 1.5 }}>
      {items.map((item, i) => (
        <Box component="li" key={i} sx={{ mb: 0.75 }}>
          <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.8 }}>
            {item}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <>
      <CssBaseline enableColorScheme />
      <Box sx={{ position: "absolute", top: { xs: 16, sm: 24 }, right: { xs: 16, sm: 24 } }}>
        <ColorModeIconDropdown />
      </Box>
      <Container maxWidth="md" sx={{ py: { xs: 6, sm: 10 } }}>
        {/* Header */}
        <Box sx={{ mb: 6, textAlign: "center" }}>
          <Box sx={{ cursor: "pointer", display: "inline-block", mb: 3 }} onClick={() => navigate("/")}>
            <SitemarkIcon />
          </Box>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
            Privacy Policy
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Effective date: {EFFECTIVE_DATE}
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Para>
          {COMPANY_NAME} ("we", "us", "our") is committed to protecting your privacy. This Privacy
          Policy explains what data we collect, how we use it, and what rights you have regarding your
          information. By using our service, you agree to the practices described here.
        </Para>

        {/* 1 */}
        <Section title="1. What Data We Collect">
          <Para>We collect the following categories of data:</Para>

          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75, color: "text.primary" }}>
            Account Information
          </Typography>
          <BulletList items={[
            "Name, email address, and company name provided at registration.",
            "Subscription plan and billing information (payment details are handled directly by Stripe and are not stored on our servers).",
            "Account settings and preferences.",
          ]} />

          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75, color: "text.primary" }}>
            Call Data
          </Typography>
          <BulletList items={[
            "Audio recordings you upload to the platform.",
            "AI-generated transcriptions of those recordings.",
            "AI-generated analysis results: scores, summaries, strengths, areas for improvement, red flags, talk ratio, and next steps.",
            "Manager notes you add to individual calls.",
          ]} />

          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75, color: "text.primary" }}>
            Team Data
          </Typography>
          <BulletList items={[
            "Salesperson profiles you create within your account (first name, last name).",
            "Performance metrics and historical score data associated with those profiles.",
          ]} />

          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75, color: "text.primary" }}>
            Usage Data
          </Typography>
          <BulletList items={[
            "Log data such as pages visited, features used, and timestamps.",
            "Browser type, operating system, and IP address.",
          ]} />
        </Section>

        {/* 2 */}
        <Section title="2. How We Use Your Data">
          <Para>We use the data we collect solely to:</Para>
          <BulletList items={[
            "Provide, operate, and maintain the service.",
            "Process and return AI-generated analysis of uploaded call recordings.",
            "Display performance metrics and historical data within your account.",
            "Send transactional emails (account confirmation, password reset, billing notifications).",
            "Respond to support requests and technical issues.",
            "Improve the reliability, security, and performance of the platform.",
          ]} />
          <Para>
            We do not sell your data. We do not use your call recordings or transcripts to train our
            AI models or any third-party AI models without your explicit written consent.
          </Para>
        </Section>

        {/* 3 */}
        <Section title="3. Third-Party Service Providers">
          <Para>
            To deliver the service, we share data with a limited set of trusted third parties. Each is
            bound by their own privacy and data protection commitments:
          </Para>
          <BulletList items={[
            "AI transcription and analysis providers — audio recordings and transcripts are sent to these providers for processing. They process data on our behalf and are not permitted to use it for their own purposes.",
            "Stripe — handles all payment processing. We do not store credit card numbers or full payment details.",
            "Cloud infrastructure providers — for secure storage and hosting of the platform and your data.",
          ]} />
          <Para>
            We do not share your data with advertisers, data brokers, or any third parties for marketing
            purposes.
          </Para>
        </Section>

        {/* 4 */}
        <Section title="4. Data Retention">
          <Para>
            We retain your data for as long as your account is active. When you cancel your account:
          </Para>
          <BulletList items={[
            "You may request an export of your data within 30 days of cancellation.",
            "After 30 days, we will permanently delete your account data, including uploaded recordings, transcripts, analysis results, and salesperson profiles.",
            "Aggregated, anonymized usage data (which cannot identify you) may be retained for analytical purposes.",
          ]} />
        </Section>

        {/* 5 */}
        <Section title="5. Data Security">
          <Para>
            We implement industry-standard security measures to protect your data, including:
          </Para>
          <BulletList items={[
            "Encryption of data in transit (TLS/HTTPS) and at rest.",
            "Access controls limiting which personnel can access customer data.",
            "Regular security assessments of our infrastructure.",
          ]} />
          <Para>
            No system is completely secure. We cannot guarantee absolute security, but we will notify
            you promptly in the event of a data breach that affects your account, as required by
            applicable law.
          </Para>
        </Section>

        {/* 6 */}
        <Section title="6. Your Rights">
          <Para>
            Depending on where you are located, you may have the following rights regarding your
            personal data:
          </Para>
          <BulletList items={[
            "Access — request a copy of the personal data we hold about you.",
            "Correction — request that we correct inaccurate or incomplete data.",
            "Deletion — request that we delete your personal data (subject to legal retention obligations).",
            "Portability — receive your data in a structured, machine-readable format.",
            "Objection — object to certain types of processing.",
          ]} />
          <Para>
            To exercise any of these rights, contact us at{" "}
            <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>. We will respond within 30
            days.
          </Para>
        </Section>

        {/* 7 */}
        <Section title="7. Cookies">
          <Para>
            We use only technically necessary cookies required for authentication and session management.
            We do not use tracking cookies, advertising cookies, or third-party analytics cookies.
          </Para>
        </Section>

        {/* 8 */}
        <Section title="8. Children's Privacy">
          <Para>
            The service is intended for business use and is not directed at individuals under the age
            of 18. We do not knowingly collect personal data from anyone under 18.
          </Para>
        </Section>

        {/* 9 */}
        <Section title="9. Changes to This Policy">
          <Para>
            We may update this Privacy Policy from time to time. We will notify you of material changes
            by email or by displaying a notice in the application at least 14 days before the changes
            take effect. Your continued use of the service after the effective date constitutes
            acceptance of the updated policy.
          </Para>
        </Section>

        {/* 10 */}
        <Section title="10. Contact">
          <Para>
            If you have questions or concerns about this Privacy Policy or how we handle your data,
            contact us at:{" "}
            <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>
          </Para>
        </Section>

        <Divider sx={{ mt: 2, mb: 4 }} />

        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <Link href="/terms" variant="body2" color="text.secondary">
            Terms of Service
          </Link>
          <Typography variant="body2" color="text.disabled">·</Typography>
          <Link
            component="button"
            variant="body2"
            color="text.secondary"
            onClick={() => navigate("/")}
          >
            Back to Home
          </Link>
        </Box>
      </Container>
    </>
  );
}
